'use client';

import {
	BigNum,
	MarketType,
	PRICE_PRECISION_EXP,
	QUOTE_PRECISION_EXP,
	ZERO,
} from '@drift-labs/sdk';
import { useCallback, useEffect, useState } from 'react';
import { useTickedAccountData } from 'src/hooks/useAccountData';
import { useCurrentAccountMarginInfo } from 'src/hooks/query/accountTradingData/useMarginInfo';
import {
	useCurrentAccountPositions,
	usePositionsByUserKey,
} from 'src/hooks/query/accountTradingData/usePositions';
import { useSwitchMarket } from 'src/hooks/driftActions/market/useSwitchMarket';
import useDriftClient from 'src/hooks/useDriftClient';
import useIsMobileScreenSize from 'src/hooks/useIsMobileScreenSize';
import useDriftAccountStore from 'src/stores/useDriftAccountsStore';
import useOnWalletChange from '../hooks/useOnWalletChange';
import useShowAccountValues from '../hooks/useShowAccountValues';
import useWalletIsConnected from '../hooks/useWalletIsConnected';
import SecondaryConnectWalletButton from './SecondaryConnectWalletButton';
import TableStateWrapper from './TableStateWrapper';
import TableV2 from './Tables/TableV2';
import NumberDisplay from './NumberDisplay/NumberDisplay';
import { getMarketPriceDecimals } from '../constants/marketDecimalConfig';
import { COMMON_UI_UTILS, OpenPosition } from '@drift/common';
import useDevSwitchIsOn from 'src/hooks/useDevSwitchIsOn';
import { usePerpMarketsSortedByMarketIndex } from 'src/hooks/perpMarkets/usePerpMarketsSortedByMarketIndex';
import SettleSelect from 'src/components/Inputs/SettleSelect';
import Button from 'src/components/primitives/Button/Button';
import useSettleAllPnls from 'src/hooks/drift-actions/useSettlePnls';
import DefinitionTooltip from 'src/components/Tooltip/DefinitionTooltip/DefinitionTooltip';

interface DisplayPosition extends OpenPosition {
	accountName: string;
	accountKey: string;
}

const UnrealizedPnlTable = () => {
	const isMobile = useIsMobileScreenSize();
	const perpMarketsSortedByMarketIndex = usePerpMarketsSortedByMarketIndex();
	const tableGridLayout = `minmax(160px,5fr) minmax(120px,5fr) minmax(130px,5fr) minmax(100px,5fr) minmax(150px,11fr) minmax(80px,6fr)`;

	const set = useDriftAccountStore((s) => s.set);

	const connected = useWalletIsConnected();
	const { switchMarket } = useSwitchMarket();
	const driftClient = useDriftClient();
	const showAccountValues = useShowAccountValues();
	const currentAccount = useTickedAccountData();
	const marginInfo = useCurrentAccountMarginInfo();
	const userKey = useDriftAccountStore((s) => s.currentUserKey);
	const openPerpPositions = useCurrentAccountPositions();
	const positionsLoaded = usePositionsByUserKey(userKey).data != null;
	const marketsNeedingSettlement = marginInfo?.marketsNeedingSettlement ?? [];
	const isDevMode = useDevSwitchIsOn();
	const { settleAllPnls, settlePnls } = useSettleAllPnls();

	const [settlingMarketIndex, setSettlingMarketIndex] = useState<
		number | undefined
	>();
	const [isSettlingAll, setIsSettlingAll] = useState(false);

	const [displayPositions, setDisplayPositions] = useState<
		DisplayPosition[] | undefined
	>();

	useOnWalletChange(() => {
		setDisplayPositions(undefined);
	});

	useEffect(() => {
		if (!currentAccount || !positionsLoaded) return;

		const normalPositions = openPerpPositions
			.filter((pos) => !pos.unsettledPnl.eq(ZERO) || !pos.baseSize.eq(ZERO))
			.map((pos) => {
				return {
					...pos,
					accountName: currentAccount.name,
					accountKey: currentAccount.userKey,
				};
			});

		setDisplayPositions(normalPositions);
	}, [openPerpPositions, positionsLoaded, currentAccount]);

	const headers = [
		'market',
		'cost basis',
		'settled',
		'unsettled funding',
		'claimable / unsettled',
		'action',
	];

	const setActiveUser = (userKey: string) => {
		const { userId, userAuthority } =
			COMMON_UI_UTILS.getIdAndAuthorityFromKey(userKey);
		if (userId !== undefined && userAuthority !== undefined) {
			driftClient?.switchActiveUser(userId, userAuthority);
		}
		set((s) => {
			s.currentUserKey = userKey;
		});
	};

	const handleClickMarket = useCallback(
		(marketIndex: number) => {
			switchMarket({
				marketIndex,
				marketType: MarketType.PERP,
				goToTradePage: true,
			});
		},
		[switchMarket]
	);

	const handleSettleAll = async () => {
		try {
			setIsSettlingAll(true);
			await settleAllPnls();
		} catch (error) {
			console.error('Error settling all pnl', error);
		} finally {
			setIsSettlingAll(false);
		}
	};

	if (isMobile) {
		return (
			<>
				{!displayPositions || displayPositions.length === 0 ? (
					<div className="flex w-full flex-col items-center justify-center rounded-sm text-center sm:h-full">
						{connected ? (
							<div className="my-8">
								<span className="typo-b3 text-text-default capitalize">
									No unsettled found
								</span>
							</div>
						) : (
							<div className="sm:pt-8">
								<SecondaryConnectWalletButton />
							</div>
						)}
					</div>
				) : (
					<div className="rounded-sm bg-container-bg">
						<div className="flex flex-col text-text-default">
							<div className="flex flex-row items-center justify-between p-3">
								<span className="typo-b3 capitalize">All Markets</span>
								<Button
									size="sm"
									variant="secondary"
									disabled={!displayPositions || !displayPositions.length}
									onClick={handleSettleAll}
									isLoading={isSettlingAll}
								>
									Settle All
								</Button>
							</div>
							<div className="flex flex-col">
								<div className="thin-scroll overflow-auto">
									{displayPositions?.map((position) => (
										<TableV2.UnsettledPnlTableCard
											onClaim={() => {
												setActiveUser(position.accountKey);
												settlePnls(
													`unrealized-pnl-table-claim-position-${
														position.marketIndex
													}-${Date.now()}`,
													position.marketIndex
												);
											}}
											position={position}
											key={`position_${position.marketSymbol}`}
										/>
									))}
								</div>
							</div>
						</div>
					</div>
				)}
			</>
		);
	}

	return (
		<TableStateWrapper
			records={displayPositions ?? []}
			emptyStateText={'No Unsettled found'}
			requireWalletConnect
			requireAccountCreated
			id="unrealised_pnl_table"
			className="thin-scroll overflow-x-auto"
		>
			<TableV2.Skeleton
				noBorder
				top={
					<TableV2.HeaderRow grid={tableGridLayout} className="pr-2">
						{headers.map((label) => (
							<TableV2.HeaderCell
								className="capitalize"
								key={`header_${label}`.replace(/ /g, '')}
							>
								{label == 'claimable / unsettled' ? (
									<DefinitionTooltip definitionId="unrealizedPnlClaimablePnl">
										{label}
									</DefinitionTooltip>
								) : (
									label
								)}
							</TableV2.HeaderCell>
						))}
					</TableV2.HeaderRow>
				}
				middle={
					<div className="w-full text-2xs xs:max-h-[40vh] sm:max-h-full">
						{displayPositions?.map((position, index) => {
							return (
								<TableV2.BodyRow
									key={`${index}_${position.marketIndex}`}
									grid={isMobile ? '' : tableGridLayout}
									className="h-13"
								>
									<TableV2.MarketCell
										key={`market__${position.marketSymbol}_${index}`}
										baseAssetSymbol={
											perpMarketsSortedByMarketIndex[position.marketIndex]
												.baseAssetSymbol
										}
										marketSymbol={position.marketSymbol}
										onClick={() => handleClickMarket(position.marketIndex)}
									/>

									<TableV2.BodyCell key={`costbasis_${index}`}>
										{!showAccountValues ? (
											'∗∗∗∗∗∗'
										) : (
											<NumberDisplay
												value={BigNum.from(
													position.costBasis,
													PRICE_PRECISION_EXP
												)}
												type="currency"
												decimals={getMarketPriceDecimals(
													perpMarketsSortedByMarketIndex[position.marketIndex]
														.baseAssetSymbol
												)}
											/>
										)}
									</TableV2.BodyCell>
									<TableV2.BodyCell key={`realizedpnl_${index}`}>
										<NumberDisplay
											type="currency"
											value={BigNum.from(
												position.realizedPnl,
												QUOTE_PRECISION_EXP
											)}
											signDependentColor
											isAccountValueToHide
										/>
									</TableV2.BodyCell>
									<TableV2.BodyCell
										className="items-center"
										key={`unsettledfunding_${index}`}
									>
										<NumberDisplay
											className="mr-1 inline-flex"
											type="currency"
											value={BigNum.from(
												position.unsettledFundingPnl,
												QUOTE_PRECISION_EXP
											)}
											signDependentColor
											isAccountValueToHide
										/>
									</TableV2.BodyCell>
									<TableV2.BodyCell
										className="inline-flex items-center"
										key={`availablepnl_${index}`}
									>
										<NumberDisplay
											className="mr-1 inline-flex"
											type="currency"
											value={BigNum.from(
												position.unsettledPnl,
												QUOTE_PRECISION_EXP
											)}
											signDependentColor
											isAccountValueToHide
										/>
										{'/'}
										<NumberDisplay
											className="ml-1 inline-flex"
											type="currency"
											value={BigNum.from(
												position.totalUnrealizedPnl,
												QUOTE_PRECISION_EXP
											)}
											isAccountValueToHide
										/>
									</TableV2.BodyCell>
									<TableV2.BodyCell key={`settlepnl_${index}`} className="py-0">
										<DefinitionTooltip
											decoration="none"
											definitionId="unrealizedPnlClaim"
										>
											<Button
												size="sm"
												variant="secondary"
												disabled={position.unsettledPnl.isZero() && !isDevMode}
												onClick={async () => {
													setActiveUser(position.accountKey);
													setSettlingMarketIndex(position.marketIndex);
													await settlePnls(
														`unrealized-pnl-table-settle-position-${
															position.marketIndex
														}-${Date.now()}`,
														position.marketIndex
													);
													setSettlingMarketIndex(undefined);
												}}
												isLoading={settlingMarketIndex === position.marketIndex}
											>
												Settle
											</Button>
										</DefinitionTooltip>
									</TableV2.BodyCell>
								</TableV2.BodyRow>
							);
						})}
					</div>
				}
				bottom={
					<div className="flex w-full items-center justify-end px-xl py-xs">
						{marketsNeedingSettlement?.length > 0 && isDevMode && (
							<div className="mr-2 max-w-[25%]">
								<SettleSelect
									options={marketsNeedingSettlement.map(
										(mktIndex) => perpMarketsSortedByMarketIndex[mktIndex]
									)}
								/>
							</div>
						)}
						<DefinitionTooltip
							definitionId="unrealizedPnlClaim"
							decoration="none"
						>
							<Button
								size="sm"
								variant="secondary"
								disabled={!displayPositions || !displayPositions.length}
								onClick={handleSettleAll}
								isLoading={isSettlingAll}
							>
								Settle All
							</Button>
						</DefinitionTooltip>
					</div>
				}
			/>
		</TableStateWrapper>
	);
};

export default UnrealizedPnlTable;
